import Grid from '@mui/material/Grid';
import type { AppInventoryOptionType } from '@openx/types/targeting/appInventory';
import {
  type IntersectOptionPayload,
  type InventoryAndContentOptionPayload,
  InventoryContentOption,
} from '@openx/types/targeting/inventoryAndContent';
import type { ComparisonType } from '@openx/types/targeting/targetingValuesTypes';
import { removeWhiteSpaces } from '@openx/utils/lib/removeWhiteSpaces';
import { type JSX, type KeyboardEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnyAction } from 'redux';
import { InventoryComparisonTypeDropdown } from '../../../shared/InventoryAndContent/components/InventoryComparisonTypeDropdown';
import { setComparisonOpValue } from '../../../shared/InventoryAndContent/inventoryHelpers';
import type { SetComparisonTypeDispatch } from '../constants';
import { InventoryInput } from '../InventoryInput';

type ComparisonProps = {
  comparisonType: ComparisonType;
  dispatchInventory: (action: AnyAction) => void;
  inventoryContent: InventoryAndContentOptionPayload | IntersectOptionPayload;
  itemsCount: number;
  setComparisonType: SetComparisonTypeDispatch;
  contentOption: InventoryContentOption | AppInventoryOptionType;
};

export const Comparison = ({
  comparisonType,
  dispatchInventory,
  inventoryContent,
  itemsCount,
  setComparisonType,
  contentOption,
}: ComparisonProps): JSX.Element => {
  const [errorText, setErrorText] = useState('');
  const { t } = useTranslation();

  const handleOnKeyUp = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!comparisonType) {
        return;
      }

      const targetElement = e.target as HTMLInputElement;
      let { value } = targetElement;

      if (e.key === 'Enter' && value) {
        const inventoryContentOptionComparisonData = inventoryContent.val[comparisonType] || [];

        value = value.trim();

        if (contentOption === InventoryContentOption.PAGE_URL) {
          value = removeWhiteSpaces(value);
        }

        const alreadyExists = inventoryContentOptionComparisonData.includes(value);
        const isEmpty = value === '';

        if (alreadyExists) {
          setErrorText(t('This item is already on the list.'));

          return false;
        }

        if (isEmpty) {
          setErrorText(t('This field cannot be empty.'));
          return false;
        }

        dispatchInventory({
          payload: { comparisonType, values: [...inventoryContentOptionComparisonData, value] },
          type: setComparisonOpValue[contentOption],
        });
        targetElement.value = '';
      }

      setErrorText('');
    },
    [comparisonType, contentOption, dispatchInventory, inventoryContent.val, t]
  );

  return (
    <Grid container spacing={1}>
      <Grid size={4}>
        <InventoryComparisonTypeDropdown option={contentOption} value={comparisonType} onChange={setComparisonType} />
      </Grid>
      <Grid size={8}>
        <InventoryInput option={contentOption} onKeyUp={handleOnKeyUp} errorText={errorText} itemsCount={itemsCount} />
      </Grid>
    </Grid>
  );
};
